<template>
  <v-card class="login-card" elevation="0">
    <!-- Logo -->
    <div class="logo-section">
      <div class="logo-ring">
        <v-avatar size="90">
          <v-img src="@/assets/icon_membership.png" alt="Membership Icon" contain></v-img>
        </v-avatar>
      </div>
      <h1 class="login-title">אזור אישי</h1>
      <p class="login-subtitle">הזן את הפרטים שלך כדי להתחבר למערכת</p>
    </div>

    <!-- Form -->
    <v-form ref="form" v-model="valid" @submit.prevent="login" class="login-form">
      <div class="field-group">
        <label class="field-label">שם משתמש</label>
        <v-text-field v-model="username" :rules="usernameRules" placeholder="הקלד שם משתמש..."
          prepend-inner-icon="mdi-account" outlined dense required :loading="progressShow" :disabled="progressShow"
          hide-details="auto" class="login-field"></v-text-field>
      </div>

      <div class="field-group">
        <label class="field-label">סיסמה</label>
        <v-text-field v-model="password" :rules="passwordRules" placeholder="הקלד סיסמה..."
          prepend-inner-icon="mdi-lock" :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          :type="showPassword ? 'text' : 'password'" @click:append="showPassword = !showPassword" outlined dense
          required :loading="progressShow" :disabled="progressShow" hide-details="auto"
          class="login-field"></v-text-field>
      </div>

      <div class="forgot-row">
        <button type="button" class="forgot-link" @click="forgotPassword">שכחתי סיסמה</button>
      </div>

      <button type="submit" class="login-btn" :disabled="!valid || progressShow" @click="login">
        <v-progress-circular v-if="progressShow" indeterminate size="20" width="2" color="white"
          class="ml-2"></v-progress-circular>
        <template v-else>
          <v-icon size="18" color="white" class="ml-2">mdi-login</v-icon>
        </template>
        כניסה למערכת
      </button>
    </v-form>

    <!-- Footer -->
    <div class="login-footer">
      <v-icon size="14" color="#CFD8DC" class="ml-1">mdi-shield-check</v-icon>
      <span>חיבור מאובטח</span>
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColorBt" :timeout="4000" top right elevation="24">
      {{ snacbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">סגור</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import ApiServices from '@/services/api.service'

export default {
  name: 'FormLogin',

  data: () => ({
    valid: false,
    username: "",
    password: "",
    showPassword: false,
    progressShow: false,
    snackbar: false,
    snackbarColorBt: "success",
    snacbarText: "",
    usernameRules: [
      v => !!v || 'שם משתמש הוא שדה חובה',
      v => v.length >= 3 || 'שם משתמש חייב להכיל לפחות 3 תווים'
    ],
    passwordRules: [
      v => !!v || 'סיסמה היא שדה חובה',
      v => v.length >= 6 || 'סיסמה חייבת להכיל לפחות 6 תווים'
    ]
  }),

  methods: {
    async login() {
      if (this.$refs.form.validate()) {
        this.progressShow = true;

        try {
          const MyJSON = JSON.stringify({
            username: this.username,
            password: this.password
          });
          const api = process.env.VUE_APP_BASE_URL + "/auth/login";

          const response = await fetch(api, ApiServices.requestOptions("POST", MyJSON, ""));
          const jsonObject = await response.json();

          switch (response.status) {
            case 200:
              this.showSnackBar("התחברת בהצלחה!", "success");
              localStorage.setItem('token', jsonObject.token);
              this.$router.replace({ name: "home" });
              break;
            case 400:
              this.showSnackBar("שגיאת התחברות: " + jsonObject.message, "error");
              break;
            case 429:
              this.showSnackBar("יותר מדי נסיונות כניסה", "warning");
              break;
            default:
              this.showSnackBar("שגיאה לא צפויה", "error");
          }
        } catch (error) {
          this.showSnackBar("שגיאת התחברות: " + error, "error");
        } finally {
          this.progressShow = false;
        }
      }
    },

    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true;
    },

    forgotPassword() {
      this.showSnackBar("בקרוב...", "info");
    }
  },

  mounted() {
    document.title = 'כניסה | משמחי לב';
  }
}
</script>

<style lang="scss" scoped>
/* CARD */
.login-card {
  background: white !important;
  border: 1px solid #e8edf2;
  border-radius: 24px !important;
  padding: 2rem 2rem 1.5rem;
  direction: rtl;
}

/* LOGO */
.logo-section {
  text-align: center;
  margin-bottom: 1.75rem;
}

.logo-ring {
  display: inline-flex;
  padding: 6px;
  border-radius: 50%;
  border: 3px solid #E3F2FD;
  margin-bottom: 0.85rem;
}

.login-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #1a2b3c;
  margin: 0 0 0.3rem;
}

.login-subtitle {
  font-size: 0.82rem;
  color: #90A4AE;
  margin: 0;
}

/* FORM */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.field-group {
  margin-bottom: 0.65rem;
}

.field-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #5a6b7c;
  margin-bottom: 0.35rem;
}

.login-field {
  &::v-deep .v-input__slot {
    border-radius: 12px !important;
    min-height: 46px;

    fieldset {
      border-color: #e2e8f0 !important;
    }
  }

  &::v-deep .v-input__slot:focus-within fieldset {
    border-color: #2196F3 !important;
  }

  &::v-deep input {
    font-size: 0.92rem;

    &::placeholder {
      color: #b0bec5;
    }
  }

  &::v-deep .v-input__prepend-inner {
    margin-right: 0;
    margin-left: 8px;
  }
}

/* FORGOT */
.forgot-row {
  text-align: start;
  margin-bottom: 1.25rem;
}

.forgot-link {
  background: none;
  border: none;
  color: #2196F3;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
}

/* LOGIN BUTTON */
.login-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 46px;
  background: linear-gradient(135deg, #2196F3, #1565C0);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 14px rgba(33, 150, 243, 0.3);
  margin-bottom: 1rem;

  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(33, 150, 243, 0.4);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

/* FOOTER */
.login-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  font-size: 0.7rem;
  color: #CFD8DC;
  font-weight: 500;
}

/* RESPONSIVE */
@media (max-width: 600px) {
  .login-card {
    padding: 1.5rem 1.25rem 1.25rem;
    border-radius: 20px !important;
  }

  .login-title {
    font-size: 1.3rem;
  }
}
</style>